import React, { useState, useEffect } from "react";
import {
    Box, Typography, CircularProgress, Table, TableHead, TableBody,
    TableRow, TableCell, Paper,
} from "@mui/material";
import moment from "moment";
import whatsappServices from "./whatsappServices";

// "2026-02" -> "feb 26"
const monthLabel = (ym) => moment(ym, "YYYY-MM").format("MMM YY");

// Celda de un valor mensual; atenúa el 0 para que resalten los meses con uso.
const ValueCell = ({ value, bold }) => (
    <TableCell
        align="center"
        sx={{
            fontSize: 13,
            fontWeight: bold ? 700 : 400,
            color: value > 0 ? "#111" : "#ccc",
            whiteSpace: "nowrap",
        }}
    >
        {value}
    </TableCell>
);

export default function WhatsappUsageReport() {
    const [loading, setLoading] = useState(false);
    const [report, setReport]   = useState(null);

    useEffect(() => {
        setLoading(true);
        whatsappServices.getUsageReport()
            .then(setReport)
            .catch(() => setReport(null))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 6 }}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    const months    = report?.months || [];
    const companies = report?.companies || [];

    if (!companies.length) {
        return (
            <Typography variant="body2" sx={{ textAlign: "center", color: "#bbb", mt: 5 }}>
                Ningún usuario registrado ha consultado el estado de un pedido por WhatsApp en los últimos 6 meses.
            </Typography>
        );
    }

    return (
        <Box sx={{ p: 2, overflow: "auto" }}>
            <Typography variant="body2" sx={{ color: "#666", mb: 1.5 }}>
                Consultas de estado de pedidos/viajes respondidas por el bot a usuarios registrados, por empresa — últimos 6 meses.
            </Typography>

            <Paper variant="outlined" sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 640 }}>
                    <TableHead>
                        <TableRow sx={{ bgcolor: "#fafafa" }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12, minWidth: 200 }}>
                                Empresa / Usuario
                            </TableCell>
                            {months.map((m) => (
                                <TableCell key={m} align="center" sx={{ fontWeight: 700, fontSize: 12, textTransform: "capitalize" }}>
                                    {monthLabel(m)}
                                </TableCell>
                            ))}
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: 12 }}>
                                Total
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {companies.map((company) => (
                            <React.Fragment key={company.company_id ?? "sin-empresa"}>
                                {/* Fila de empresa (totales) */}
                                <TableRow sx={{ bgcolor: "#ede7f6" }}>
                                    <TableCell sx={{ fontWeight: 700, fontSize: 13, color: "#5e35b1" }}>
                                        {company.company_name}
                                    </TableCell>
                                    {months.map((m) => (
                                        <ValueCell key={m} value={company.totals?.[m] || 0} bold />
                                    ))}
                                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: 13, color: "#5e35b1" }}>
                                        {company.total}
                                    </TableCell>
                                </TableRow>

                                {/* Filas de usuarios */}
                                {company.users.map((u) => (
                                    <TableRow key={u.user_id} hover>
                                        <TableCell sx={{ pl: 3, fontSize: 13 }}>
                                            {u.name}
                                            {u.phone && (
                                                <Typography component="span" sx={{ color: "#999", fontSize: 11, ml: 0.5 }}>
                                                    · {u.phone}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        {months.map((m) => (
                                            <ValueCell key={m} value={u.monthly?.[m] || 0} />
                                        ))}
                                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: 13 }}>
                                            {u.total}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </React.Fragment>
                        ))}
                    </TableBody>
                </Table>
            </Paper>
        </Box>
    );
}
